import React, { useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../design/AppContext";
import { hasPermission } from "../../utils/permission";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import { RowButton, StatusChip } from "../../components/mui";

/**
 * 我的帳號（設計稿 #screen-me，el-pm-template_4.html:2195-2238）
 *
 * 右上角人名點下去的地方。舊系統把「我是誰」拆在三個地方：側欄左下角的頭像、
 * 側欄左下角那顆 16px 的小鎖頭（改密碼）、以及系統設定裡的偏好設定。
 * 這一頁把它們收成一份選單，登出也從側欄底部搬到這裡。
 *
 * 版面規則：
 *   · 整頁沒有 contained 主要按鈕 —— 這一頁沒有「主要動作」，
 *     唯一醒目的按鈕是登出，它是 outlined color="error"（硬規則 1）。
 *   · 不自己包頁面內距，Layout 的內容區已經給了。
 *
 * ── 後端沒有的欄位（NONE-AVAILABLE，硬規則 10）────────────────────
 * 設計稿 L2200 的「可以看 5 個專案 · 上次登入：今天 08:41（這台電腦）」
 * 與 L2214 的「上次更改：6 月 3 日」全部整段不畫：
 *   · 可看專案數 —— /Auth/Admin/List 與 access_token 都沒有這個數字。
 *     /Project/List 回的是「我看得到的專案」，拿它的筆數來充數會在
 *     有搜尋條件或分頁時直接變成錯的數字。
 *   · 上次登入 —— admin 資料表只有 create_at / update_at，
 *     update_at 是「這筆資料上次被改的時間」，不是登入時間。
 *   · 上次改密碼 —— 同上，沒有獨立欄位。
 * 後端補上之後直接加進 RowButton 的 meta 即可，版面不用動。
 */

/** 選單列（設計稿 L2204-2231）。整列可點，遠大於 44px 而且一定有文字（硬規則 3） */
function MenuRow({ title, meta, tag, onClick }) {
  return (
    <RowButton
      type="button"
      onClick={onClick}
      title={title}
      meta={<span>{meta}</span>}
      side={tag ? <StatusChip kind="type">{tag}</StatusChip> : null}
    />
  );
}

MenuRow.propTypes = {
  title: PropTypes.string.isRequired,
  meta: PropTypes.string.isRequired,
  tag: PropTypes.string,
  onClick: PropTypes.func,
};
MenuRow.defaultProps = { tag: "", onClick: () => {} };

/**
 * 登出確認（設計稿 L2610-2621 的 #overlay-logout，文案逐字照抄）。
 *
 * 為什麼不用 useApp() 的 confirmDelete：那一支的語氣是「刪除，無法復原」，
 * 而登出不是毀損性操作，只是要再登入一次。用同一個紅色刪除對話框問
 * 「要登出嗎」會把兩件嚴重程度差很多的事講成一樣。
 */
function LogoutDialog({ busy, onCancel, onConfirm }) {
  /* Esc 與點遮罩都走 onClose：鍵盤使用者要有一條不用找「取消」鈕的退路。
     寬度 480px 照設計稿 L2612（maxWidth="xs" 正好 444～480），窄螢幕自己縮。 */
  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth role="alertdialog" aria-labelledby="lo-title">
      <DialogTitle id="lo-title" variant="h2" component="h2">
        確定要登出嗎？
      </DialogTitle>
      <DialogContent>
        <Typography>登出後須重新輸入帳號密碼才能登入。尚未送出的回報將會保留。</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" type="button" onClick={onCancel}>
          取消
        </Button>
        <Button variant="contained" color="error" type="button" onClick={onConfirm} aria-disabled={busy} loading={busy} loadingPosition="start">
          {busy ? "登出中…" : "登出"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

LogoutDialog.propTypes = {
  busy: PropTypes.bool,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
};
LogoutDialog.defaultProps = { busy: false, onCancel: () => {}, onConfirm: () => {} };

function MeIndex(props) {
  const navigate = useNavigate();
  const { userInfo } = useApp();
  const { Logout, canManageAdmin } = props;

  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  /*
   * 姓名、Email、角色都來自 access_token 的 claim（sagas/Global.js 的 INITIAL
   * 把整個 token payload 存成 Global.userInfo）。後端登入時就把 role 的名稱
   * 放進 claim 了，所以這三個都是真的，不是從別的欄位推出來的。
   */
  const name = _.get(userInfo, "name", _.get(userInfo, "Name", ""));
  const email = _.get(userInfo, "email", _.get(userInfo, "Email", ""));
  const account = _.get(userInfo, "account", _.get(userInfo, "Account", ""));
  const role = _.get(userInfo, "role", _.get(userInfo, "Role", ""));

  const doLogout = () => {
    if (busy) return;
    setBusy(true);
    Logout(() => navigate("/Auth/Login", { replace: true }), setBusy);
  };

  return (
    <>
      <Typography variant="h1" component="h1">
        我的帳號
      </Typography>

      {/* ---------- 我是誰（設計稿 L2197-2201） ---------- */}
      <Card component="section" sx={{ mt: "var(--space-6)", maxWidth: 640 }}>
        <CardContent>
          {/* 姓名取不到就退回帳號，再取不到就整行不畫 —— 不要用「？」或「使用者」充數 */}
          {name || account ? <Typography variant="h2">{name || account}</Typography> : null}
          {email ? (
            <Typography sx={{ mt: "var(--space-2)" }}>{email}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: "var(--space-2)" }}>
              尚未填寫 Email，可至「我的資料與通知」補填。
            </Typography>
          )}
          {role ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: "var(--space-2)" }}>
              角色：{role}
            </Typography>
          ) : null}
        </CardContent>
      </Card>

      {/* ---------- 選單（設計稿 L2203-2232） ---------- */}
      <Card sx={{ mt: "var(--space-6)", maxWidth: 640 }}>
        <List disablePadding>
          <MenuRow
            title="我的資料與通知"
            meta="姓名、Email、通知設定、字級與深淺色"
            onClick={() => navigate("/Setting/Preferences")}
          />
          <MenuRow title="變更密碼" meta="變更登入密碼" onClick={() => navigate("/Me/Password")} />
          <MenuRow title="新手指南" meta="三項常用操作的步驟說明" onClick={() => navigate("/Guide")} />
          {/* 沒有 admin 權限的人整列不畫：畫出來按下去只會撞到「你沒有權限」 */}
          {canManageAdmin ? (
            <MenuRow
              title="帳號與角色管理"
              meta="僅系統管理員可見"
              tag="管理員"
              onClick={() => navigate("/Setting/Admin")}
            />
          ) : null}
        </List>
      </Card>

      {/* ---------- 登出（設計稿 L2234-2237） ---------- */}
      {/*
        全頁唯一的紅色按鈕。登出從側欄底部搬到這裡之後，
        layouts/commons/Sider.js 底部那顆暫留的登出鈕就可以拿掉了
        —— 那支檔案不在這次的清單裡，留給負責側欄的人處理。
        在它拿掉之前兩個入口並存不會壞，兩邊都是 dispatch 同一個 Logout。
      */}
      <Box sx={{ mt: "var(--space-6)" }}>
        <Button variant="outlined" color="error" type="button" onClick={() => setAsking(true)}>
          登出
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: "var(--space-2)" }}>
          登出後須重新輸入帳號密碼。尚未完成的回報將會保留。
        </Typography>
      </Box>

      {asking ? <LogoutDialog busy={busy} onCancel={() => setAsking(false)} onConfirm={doLogout} /> : null}
    </>
  );
}

MeIndex.propTypes = {
  Logout: PropTypes.func,
  canManageAdmin: PropTypes.bool,
};

MeIndex.defaultProps = { Logout: () => {}, canManageAdmin: false };

/* 「帳號與角色管理」那一列的顯隱看 access_token 的 permission claim；
   真正的把關在後端 AuthController 的 [PermissionOnly("admin")] */
const mapStateToProps = () => ({
  canManageAdmin: hasPermission("admin"),
});

const mapDispatchToProps = (dispatch) => ({
  Logout: (callback, loading) => dispatch({ type: "Logout", callback, loading }),
});

export default connect(mapStateToProps, mapDispatchToProps)(MeIndex);
